import type { BoundLabel } from '@examdojo/core/widget';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import type { EdgePort, PointPort } from '../../../port/port.model';
import type { ArrowAnnotationElement } from './arrow-annotation.model';
import { ARROW_ANNOTATION_DEFAULTS } from './arrow-annotation.model';

/** Edge-targeting variant: dashed line with circle endpoint in neutral-800. */
const EDGE_TARGET_DEFAULTS = {
  color: 'neutral-800' as const,
  dash: 2 as const,
  circleSize: 3,
};

/**
 * Create an arrow annotation using the core arrowAnnotation factory.
 * Renders a label connected to a target point by a routed arrow path.
 *
 * When the target resolves to an edge, uses a different visual variant:
 * dashed line with a circle endpoint, defaulting to neutral-800.
 */
export function createArrowAnnotation(
  element: ArrowAnnotationElement & { id: string },
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig } = context;
  const labels: BoundLabel[] = [];

  // Resolve target — detect edge targets for variant styling
  const rawTarget = portRegistry.resolve(element.params.target);
  const isEdgeTarget =
    typeof rawTarget === 'object' && rawTarget !== null && 'start' in rawTarget && 'end' in rawTarget;

  let targetPoint: PointPort;
  if (isEdgeTarget) {
    const edge = rawTarget as EdgePort;
    targetPoint = [(edge.start[0] + edge.end[0]) / 2, (edge.start[1] + edge.end[1]) / 2];
  } else {
    targetPoint = portRegistry.resolvePoint(element.params.target);
  }

  const offset = element.params.offset ?? ARROW_ANNOTATION_DEFAULTS.offset;
  const routing = element.params.routing ?? ARROW_ANNOTATION_DEFAULTS.routing;
  const labelColor = element.color ?? ARROW_ANNOTATION_DEFAULTS.color;
  const lineColor = isEdgeTarget ? EDGE_TARGET_DEFAULTS.color : labelColor;

  // Per-element display overrides
  const elementDisplay = displayConfig.elements?.[element.id];

  // Label position = target + offset
  const labelPos: [number, number] = [targetPoint[0] + offset[0], targetPoint[1] + offset[1]];

  // Label config: style editor with showLabel on 'line' section puts label at line.label
  const lineLabel = elementDisplay?.line?.label;
  const lineLabelConfig = typeof lineLabel === 'object' && lineLabel ? lineLabel : {};

  const result = elementFactory.arrowAnnotation({
    from: labelPos,
    to: targetPoint,
    text: element.params.label,
    routing,
    style: {
      line: {
        color: elementDisplay?.line?.color ?? lineColor,
        strokeWidth: elementDisplay?.line?.strokeWidth ?? 1.5,
        ...(isEdgeTarget ? { dash: EDGE_TARGET_DEFAULTS.dash, arrows: { last: false } } : {}),
        ...(elementDisplay?.line ?? {}),
      },
      label: {
        color: labelColor,
        ...lineLabelConfig,
        ...(elementDisplay?.label ?? {}),
      },
      visible: elementDisplay?.visible ?? true,
    },
  });

  const jsxObjects = [...result.objects];
  if (result.label) {
    labels.push(result.label);
  }

  // Edge variant: add a visible circle point at the target instead of an arrowhead
  if (isEdgeTarget) {
    const circleEnd = elementFactory.point({
      coords: targetPoint,
      style: {
        visible: true,
        fixed: true,
        face: 'o',
        size: EDGE_TARGET_DEFAULTS.circleSize,
        color: elementDisplay?.line?.color ?? lineColor,
        label: false,
      },
    });
    jsxObjects.push(circleEnd.point);
  }

  return {
    jsxObjects,
    labels,
    ports: {},
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}
